import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  Button, FormControl, FormLabel, Input, Select, Flex, Text, Alert, AlertIcon,
  Spinner, Box,
} from '@chakra-ui/react';
import * as FiIcons from 'react-icons/fi';
import { createContentCreator } from '../../../../../store/contentCreators/contentCreatorsSlice';
import theme from '../../../../global/theme';
import axiosInstance from '../../../../../utilities/axios';

const PLATFORMS = ['youtube', 'twitch', 'discord', 'facebook', 'instagram', 'tiktok'];

const CreateModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const contentCreators = useSelector(state => state.contentCreators);
  const token = useSelector(state => state.auth.token);
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    defaultValues: { primary_platform: 'youtube' }
  });
  const [platformRows, setPlatformRows] = useState([]);
  const [fetchingStats, setFetchingStats] = useState(false);
  const [fetchedStats, setFetchedStats] = useState(null);
  const debounceRef = useRef(null);

  const addPlatform = () => setPlatformRows(prev => [...prev, { type: 'twitch', url: '', subs: '' }]);
  const removePlatform = (i) => setPlatformRows(prev => prev.filter((_, idx) => idx !== i));
  const updatePlatform = (i, field, value) =>
    setPlatformRows(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));

  const youtubeChannelUrl = watch('youtube_channel_url');

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setFetchedStats(null);
    if (!youtubeChannelUrl || !youtubeChannelUrl.includes('youtu')) return;
    debounceRef.current = setTimeout(async () => {
      setFetchingStats(true);
      try {
        const res = await axiosInstance.get(
          `/api/fetch_youtube_channel_stats?url=${encodeURIComponent(youtubeChannelUrl)}`,
          { headers: { Authorization: token } }
        );
        if (res.data.success) {
          const { title, handle, avatar, banner, followers, views, videos_count, joined_year } = res.data.data;
          if (title) setValue('name', title);
          if (handle) setValue('tag', handle);
          setValue('followers', followers);
          setValue('videos_count', videos_count);
          setValue('joined_year', joined_year);
          setValue('views', views);
          setValue('youtube_avatar_url', avatar || '');
          setValue('youtube_banner_url', banner || '');
          setFetchedStats({ title, handle, avatar, banner, followers, views, videos_count, joined_year });
        }
      } catch (_) {
        // silently ignore
      } finally {
        setFetchingStats(false);
      }
    }, 800);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeChannelUrl, setValue, token]);

  const onSubmit = (values) => {
    const fd = new FormData();
    if (values.avatar?.[0]) fd.append('avatar', values.avatar[0]);
    fd.append('youtube_avatar_url', values.youtube_avatar_url || '');
    if (values.banner?.[0]) fd.append('banner', values.banner[0]);
    fd.append('youtube_banner_url', values.youtube_banner_url || '');
    fd.append('name', values.name);
    fd.append('tag', values.tag || '');
    fd.append('primary_platform', values.primary_platform);
    fd.append('followers', values.followers || '0');
    fd.append('views', values.views || '0');
    fd.append('videos_count', values.videos_count || 0);
    fd.append('joined_year', values.joined_year || '');
    const platforms = [...platformRows];
    if (values.youtube_channel_url) {
      platforms.unshift({ type: 'youtube', url: values.youtube_channel_url, subs: values.followers || '0' });
    }
    fd.append('platforms', JSON.stringify(platforms));
    dispatch(createContentCreator(fd)).unwrap().then(() => onClose());
  };

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius={4} paddingBlock={4} bg={theme.dark}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader textAlign="center" color="orange" fontSize={20}>إضافة صانع محتوى</ModalHeader>
          <ModalBody>
            {contentCreators.errors.length > 0 && (
              <Alert status="error" variant="left-accent" marginBottom={4}>
                <AlertIcon />
                {contentCreators.errors.map((e, i) => <Text key={i}>{e?.msg}</Text>)}
              </Alert>
            )}
            <input type="hidden" {...register('youtube_avatar_url')} />
            <input type="hidden" {...register('youtube_banner_url')} />
            <input type="hidden" {...register('followers')} />

            <FormControl mb={2}>
              <Flex align="center" gap={2} mb={1}>
                <FormLabel color={theme.light} mb={0}>رابط قناة اليوتيوب</FormLabel>
                {fetchingStats && <Spinner size="xs" color="orange.300" />}
              </Flex>
              <Input bg={theme.light} border="none" borderRadius={4}
                placeholder="https://www.youtube.com/@channel" {...register('youtube_channel_url')} />
              <Text color="whiteAlpha.600" fontSize="xs" mt={1}>
                أدخل رابط القناة وسيتم جلب الاسم، التاق، الصورة، المشاهدات، المتابعين، عدد الفيديوهات وسنة الانضمام تلقائياً
              </Text>
            </FormControl>
            {fetchedStats && !fetchingStats && (
              <Box mb={4} p={2} bg="whiteAlpha.100" borderRadius={4} fontSize="sm" color={theme.light}>
                {fetchedStats.banner && (
                  <img src={fetchedStats.banner} alt="banner"
                    style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4, marginBottom: 8 }} />
                )}
                <Flex gap={3} align="center">
                  {fetchedStats.avatar && (
                    <img src={fetchedStats.avatar} alt={fetchedStats.title}
                      style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
                  )}
                  <Flex direction="column" gap={1}>
                    {fetchedStats.title && <Text fontWeight="bold">{fetchedStats.title}</Text>}
                    <Flex gap={4} flexWrap="wrap">
                      <Text>👤 {fetchedStats.followers}</Text>
                      <Text>👁 {fetchedStats.views}</Text>
                      <Text>🎬 {fetchedStats.videos_count}</Text>
                      {fetchedStats.joined_year && <Text>📅 {fetchedStats.joined_year}</Text>}
                    </Flex>
                  </Flex>
                </Flex>
              </Box>
            )}

            <FormControl mb={4}>
              <FormLabel color={theme.light}>الاسم *</FormLabel>
              <Input bg={theme.light} border="none" borderRadius={4}
                {...register('name', { required: 'مطلوب' })} />
              {errors.name && <Text color="red.400" mt={1}>{errors.name.message}</Text>}
            </FormControl>
            <Flex gap={4} mb={4}>
              <FormControl>
                <FormLabel color={theme.light}>التاق (@username)</FormLabel>
                <Input bg={theme.light} border="none" borderRadius={4} {...register('tag')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light}>المنصة الرئيسية</FormLabel>
                <Select bg={theme.light} border="none" borderRadius={4} {...register('primary_platform')}>
                  {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                </Select>
              </FormControl>
            </Flex>
            <Flex gap={4} mb={4}>
              <FormControl>
                <FormLabel color={theme.light}>المشاهدات</FormLabel>
                <Input bg={theme.light} border="none" borderRadius={4} placeholder="1.2M" {...register('views')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light}>عدد الفيديوهات</FormLabel>
                <Input type="number" bg={theme.light} border="none" borderRadius={4} {...register('videos_count')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light}>سنة الانضمام</FormLabel>
                <Input bg={theme.light} border="none" borderRadius={4} placeholder="2023" {...register('joined_year')} />
              </FormControl>
            </Flex>
            <FormControl mb={4}>
              <FormLabel color={theme.light}>الصورة الشخصية</FormLabel>
              <Input type="file" accept="image/*" bg={theme.light} border="none" borderRadius={4}
                p={1} {...register('avatar')} />
              <Text color="whiteAlpha.600" fontSize="xs" mt={1}>
                اتركها فارغة لجلب صورة القناة تلقائياً من رابط اليوتيوب أعلاه
              </Text>
            </FormControl>
            <FormControl mb={4}>
              <FormLabel color={theme.light}>صورة الغلاف (Banner)</FormLabel>
              <Input type="file" accept="image/*" bg={theme.light} border="none" borderRadius={4}
                p={1} {...register('banner')} />
              <Text color="whiteAlpha.600" fontSize="xs" mt={1}>
                اتركها فارغة لجلب غلاف القناة تلقائياً من رابط اليوتيوب أعلاه
              </Text>
            </FormControl>

            <Text color={theme.light} fontWeight="bold" mb={2}>منصات إضافية (اختياري)</Text>
            {platformRows.map((p, i) => (
              <Flex key={i} gap={3} mb={3} alignItems="center">
                <Select value={p.type} onChange={e => updatePlatform(i, 'type', e.target.value)}
                  bg={theme.light} border="none" borderRadius={4} w="150px">
                  {PLATFORMS.map(pl => <option key={pl} value={pl}>{pl}</option>)}
                </Select>
                <Input placeholder="رابط" value={p.url} onChange={e => updatePlatform(i, 'url', e.target.value)}
                  bg={theme.light} border="none" borderRadius={4} />
                <Button size="sm" colorScheme="red" onClick={() => removePlatform(i)}>
                  <FiIcons.FiMinus />
                </Button>
              </Flex>
            ))}
            <Button size="sm" colorScheme="blue" onClick={addPlatform} mb={4}>
              <FiIcons.FiPlus /> إضافة منصة
            </Button>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap={3}>
              <Button type="submit" rightIcon={<FiIcons.FiSave />} colorScheme="green"
                isLoading={contentCreators.isLoading}>حفظ</Button>
              <Button type="button" rightIcon={<FiIcons.FiX />} colorScheme="red"
                isLoading={contentCreators.isLoading} onClick={onClose}>إلغاء</Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default CreateModal;
